// Drugs › All maps, the sheet: every drug of the Drug list on its primary target, the viewport's own frames in a grid on one
// 0–1 scale (occupancy × relative receptor density, the Gallery figure's measure). The tiles fill as the viewport paints
// them; a click opens that drug on that target. On the 2D preview the tiles carry the numbers and the sheet says the maps
// need the 3D viewport.

import { useCallback, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAtlas } from '../app/store.ts';
import { IconButton } from '../ui/primitives.tsx';
import { colormapStops, gradientCss } from '../ui/scales.ts';
import { TARGET_LABEL } from './Traces.tsx';
import { auditVerdict, baselineFor, MAPS_RANGE, PET_AUDIT, petLine, tileLines, useDrugMaps, type MapTile } from './drugMaps.ts';

export function DrugMaps({ onClose }: { onClose: () => void }) {
  const tiles = useDrugMaps((s) => s.tiles);
  const status = useDrugMaps((s) => s.status);
  const why = useDrugMaps((s) => s.why);
  const painted = useDrugMaps((s) => s.painted);
  const size = useDrugMaps((s) => s.size);
  const pack = useDrugMaps((s) => s.pack);
  // The drawn cortex's own name, from its hemisphere surface ("Left cerebral cortex (BigBrain)" → BigBrain).
  const brainName = useAtlas((s) => (s.brain && s.brain.pack === pack ? (/\(([^)]+)\)/.exec(s.brain.names[0] ?? '')?.[1] ?? s.brain.pack) : null));
  const setChoice = useAtlas((s) => s.setChoice);
  const setTraceView = useAtlas((s) => s.setTraceView);
  const current = useAtlas((s) => s.choices.drug);
  const sheet = useCallback((el: HTMLElement | null) => el?.focus(), []);
  // Esc closes the sheet before the app's shortcuts see the key.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  const pick = (t: MapTile) => {
    setChoice('drug', t.value);
    if (t.target) setTraceView({ target: t.target });
    onClose();
  };
  const n = tiles.length;
  const line =
    status === 'loading'
      ? 'Reading the runs’ headers…'
      : status === 'painting'
        ? `Painting ${Math.min(painted + 1, n)} of ${n}…`
        : status === 'done'
          ? `${painted} of ${n} painted from where you’re looking${brainName ? `, on ${brainName}` : ''}`
          : status === 'none'
            ? (why ?? '')
            : status === 'error'
              ? (why ?? 'The maps didn’t paint.')
              : '';
  const frame = { aspectRatio: size ? `${size.width} / ${size.height}` : '16 / 10' };
  return (
    <div className="sheet-overlay" onClick={onClose} role="presentation">
      <section className="sheet drug-maps" role="dialog" aria-label="All drugs’ maps" aria-modal="true" tabIndex={-1} ref={sheet} data-label-exclude="" data-status={status} onClick={(e) => e.stopPropagation()}>
        <header className="sheet-head">
          <div>
            <h4>Where the drugs meet the cortex</h4>
            <p className="ba-type-caption muted">
              {`${n || 'The'} drugs of the Drug list, each on its primary target: the predicted occupancy at the run’s own scenario × Hansen 2022’s relative receptor density (Schaefer-400), painted on the cortex the viewport draws · one scale, 0–1`}
            </p>
          </div>
          <IconButton icon={X} label="Close" onClick={onClose} />
        </header>
        <div className="maps-bar">
          <span className="ba-type-caption muted">Engagement · occupancy × relative density</span>
          <span className="strip" style={{ background: gradientCss(colormapStops('viridis')) }} />
          <span className="mono ba-type-caption">{MAPS_RANGE[0]}</span>
          <span className="mono ba-type-caption">{MAPS_RANGE[1]}</span>
        </div>
        <p className="ba-type-caption muted maps-status" aria-live="polite">
          {line}
        </p>
        <ul className="maps-grid">
          {tiles.map((t) => {
            const { what, how } = tileLines(t, TARGET_LABEL);
            const empty = t.status === 'painting' ? 'Painting…' : t.status === 'waiting' ? (status === 'none' ? 'Needs the 3D viewport' : '…') : (t.why ?? '');
            return (
              <li key={t.value} className={`map-tile ${t.status}${t.value === current ? ' current' : ''}`} data-drug={t.value.replace(/^pharma\//, '')} data-status={t.status} data-shows={t.shows} data-mean={t.mean !== null ? t.mean.toFixed(4) : ''}>
                <button type="button" className="map-pick" onClick={() => pick(t)} title={`${t.name}${t.scenario ? ` · ${t.scenario}` : ''} · ${what} · ${how} · open this drug on this target`}>
                  <span className="map-frame" style={frame}>
                    {t.url ? <img src={t.url} alt={`${t.name}: ${what}, on the cortex`} /> : <span className="map-empty ba-type-caption muted">{empty}</span>}
                  </span>
                  <span className="map-name">{t.name}</span>
                  <span className="map-what">{what}</span>
                  <span className="map-how muted" title={how}>
                    {how}
                  </span>
                  <span className="map-pet muted" data-pet={baselineFor(t.value) ? 'audited' : 'none'}>
                    {petLine(t.value)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <p className="ba-type-caption muted maps-foot">
          {`Each tile is the viewport’s own frame with that map pushed to the cortex’s vertices (the receptor table through the pack’s Schaefer-400 labels); a drug with no binding modelled shows its target’s density alone. The occupancy is the frozen exposure–affinity model’s prediction at the run’s own scenario (the Compare sheet’s number), not a measurement: ba-pharm’s audit (${PET_AUDIT.note} §1) scores it against measured human PET where a study exists. ${auditVerdict()} Each tile says where its drug stands, in occupancy percentage points of mean absolute error, or that there is no measured PET to compare. A click opens the drug on that target.`}
        </p>
      </section>
    </div>
  );
}
